import * as React from 'react';

import {
  MdAdd,
  MdCheckCircle,
  MdClose,
  MdDelete,
  MdEdit,
  MdPauseCircle,
  MdSearch,
  MdShield,
  MdVisibility,
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import DataGrid from '../../Components/Table/DataGrid';
import DeleteModel from '../../Components/Common/DeleteModel';
import { getAllRoles, deleteRole } from '../../Services/RolesService';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes, isSuperAdminUser } from '../../Utils/Permissions';
import { APPRoutes } from '../../Utils/Route';
import { toast } from 'react-toastify';
import PageTitle from '../../Components/Common/PageTitle';
import SearchableDropdown from '../../Components/Subscription/SearchableDropdown';

const STATUS_OPTIONS = ['All', 'Active', 'Inactive'];
const STATUS_DROPDOWN_OPTIONS = STATUS_OPTIONS.map((option) => ({
  value: option,
  label: option,
}));
const STATUS_BADGE_STYLE = {
  Active: { backgroundColor: 'var(--hc-status-success-bg)', color: 'var(--hc-status-success-text)' },
  Inactive: { backgroundColor: 'var(--hc-status-inactive-bg)', color: 'var(--hc-status-inactive-text)' },
};

const SUPER_ADMIN_ROLE = 'Super Admin';

export default function RolesList() {
  const navigate = useNavigate();
  const { token, permissions, decodedToken } = useAuth();

  const userPermissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken],
  );

  const isSuperAdmin = isSuperAdminUser(decodedToken);

  const canViewRole = permissionHas(userPermissionCodes, PermissionCodes.ROLES_VIEW, decodedToken);
  const canCreateRole = permissionHas(userPermissionCodes, PermissionCodes.ROLES_CREATE, decodedToken);
  const canEditRole = permissionHas(userPermissionCodes, PermissionCodes.ROLES_EDIT, decodedToken);
  const canDeleteRole = permissionHas(userPermissionCodes, PermissionCodes.ROLES_DELETE, decodedToken);

  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState('All');
  const [rows, setRows] = React.useState([]);
  const [statusSummary, setStatusSummary] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState('');

  const loadRoles = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllRoles(token);
      if (response?.is_success) {
        const roles = Array.isArray(response?.data?.Roles) ? response.data.Roles : [];
        setRows(roles);
        setStatusSummary(response?.data?.StatusSummary || null);
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal server error. Please Contact Support Team.');
        setRows([]);
        setStatusSummary(null);
      } else{
        toast.error(response?.message || 'Failed to load roles');
        setRows([]);
        setStatusSummary(null);
      }
    } catch (error) {
      console.error(error);
      setRows([]);
      setStatusSummary(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => {
    if (token) {
      loadRoles();
    }
  }, [token, loadRoles]);

  const handleCreateRole = React.useCallback(() => {
    navigate(APPRoutes.ROLES_FORM);
  }, [navigate]);

  const handleView = React.useCallback((row) => {
    navigate(APPRoutes.ROLES_VIEW.replace(':id', row.Id), { state: { role: row } });
  }, [navigate]);

  const handleEdit = React.useCallback((row) => {
    navigate(APPRoutes.ROLES_EDIT.replace(':id', row.Id));
  }, [navigate]);

  const handleDeleteClick = React.useCallback((row) => {
    setDeleteError('');
    setDeleteTarget(row);
  }, []);

  const handleDeleteClose = React.useCallback(() => {
    if (deleteLoading) {
      return;
    }
    setDeleteTarget(null);
    setDeleteError('');
  }, [deleteLoading]);

  const handleDeleteConfirm = React.useCallback(async () => {
    if (!deleteTarget?.Id) {
      return;
    }
    try {
      setDeleteLoading(true);
      setDeleteError('');
      try {
        const response = await deleteRole(deleteTarget.Id, token);
        if (response?.is_success) {
          setRows((prev) => prev.filter((role) => role.Id !== deleteTarget.Id));
          setDeleteTarget(null);
          loadRoles();
          toast.success(response?.message || 'Role deleted successfully.');
        } else if (response?.is_success === false && response?.status_code === 500) {
          setDeleteError("Internal Server Error. Please Contact Support Team.");
        } else {
          setDeleteError(response?.message || 'Failed to delete role.');
        }
      } catch (error) {
        console.error(error);
        setDeleteError('Something went wrong while deleting the role.');
      }
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteTarget, token, loadRoles]);

  const filteredRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();

    return rows.filter((row) => {
      const rowStatus = row.Status || '';
      const matchesStatus = status === 'All' || rowStatus === status;

      const matchesSearch =
        !term ||
        row.Name?.toLowerCase().includes(term) ||
        row.AccountName?.toLowerCase().includes(term);

      return matchesStatus && matchesSearch;
    });
  }, [rows, search, status]);

  const stats = React.useMemo(() => {
    const counts = statusSummary || rows.reduce((acc, row) => {
      const rowStatus = row.Status || 'Unknown';
      acc[rowStatus] = (acc[rowStatus] || 0) + 1;
      acc.Total = (acc.Total || 0) + 1;
      return acc;
    }, {});

    return [
      {
        label: 'Total Roles',
        value: counts.Total ?? rows.length,
        icon: <MdShield size={22} color="var(--hc-icon-blue-text)" />,
        iconBg: 'var(--hc-icon-blue-bg)',
        accentClass: 'border-primary',
        valueClass: 'text-primary',
      },
      {
        label: 'Active',
        value: counts.Active || 0,
        icon: <MdCheckCircle size={22} color="var(--hc-status-success-text)" />,
        iconBg: 'var(--hc-icon-green-bg)',
        accentClass: 'border-success',
        valueClass: 'text-success',
      },
      {
        label: 'Inactive',
        value: counts.Inactive || 0,
        icon: <MdPauseCircle size={22} color="var(--hc-status-warning-text)" />,
        iconBg: 'var(--hc-status-warning-bg)',
        accentClass: 'border-secondary',
        valueClass: 'text-secondary',
      },
    ];
  }, [rows, statusSummary]);

  const columns = React.useMemo(() => {
    const cols = [
      {
        id: 'Name',
        label: 'Role',
        minWidth: 50,
        format: (value, row) => canViewRole ? (
          <button
            type="button"
            className="btn p-0 text-start text-dark border-0 bg-transparent"
            style={{ font: 'inherit' }}
            onClick={() => handleView(row)}
          >
            <div className="d-flex flex-column">
              <span className="fw-semibold text-dark">{value}</span>
              {row.Description ? (
                <span className="text-secondary small">{row.Description}</span>
              ) : null}
            </div>
          </button>
        ) : (
          <div className="d-flex flex-column">
            <span className="fw-semibold text-dark">{value}</span>
            {row.Description ? (
              <span className="text-secondary small">{row.Description}</span>
            ) : null}
          </div>
        ),
      },
    ];

    if (isSuperAdmin) {
      cols.push({
        id: 'AccountName',
        label: 'Client',
        minWidth: 100,
        format: (value) => value || <span className="text-secondary">—</span>,
      });
    }

    cols.push(
      {
        id: 'PermissionsCount',
        label: 'Permissions',
        minWidth: 50,
        align: 'center',
        format: (value) => value ?? 0,
      },
      {
        id: 'Status',
        label: 'Status',
        minWidth: 50,
        align: 'center',
        format: (value) => (
          <span
            className="badge rounded-pill fw-bolder px-3 py-2 text-center"
            style={{
              minWidth: '96px',
              fontWeight: 1000,
              ...(STATUS_BADGE_STYLE[value] || {
                backgroundColor: 'var(--hc-status-inactive-bg)',
                color: 'var(--hc-status-inactive-text)',
              }),
            }}
          >
            {value}
          </span>
        ),
      },
      {
        id: 'CreatedBy',
        label: 'Created By',
        minWidth: 70,
      },
    );

    if (canViewRole || canEditRole || canDeleteRole) {
      cols.push({
        id: 'actions',
        label: 'Actions',
        minWidth: 80,
        align: 'center',
        format: (_value, row) => (
          <div className="d-flex align-items-center justify-content-center gap-2">
            {canViewRole ? (
              <button
                type="button"
                title="View"
                className="btn btn-sm p-1 lh-1 text-primary border-0 rounded-2"
                style={{ backgroundColor: 'var(--hc-status-info-bg)' }}
                onClick={() => handleView(row)}
              >
                <MdVisibility size={16} />
              </button>
            ) : null}
            {canEditRole ? (
              <button
                type="button"
                title="Edit"
                className="btn btn-sm p-1 lh-1 hc-text-primary border-0 rounded-2"
                style={{ backgroundColor: 'var(--hc-icon-blue-bg)' }}
                onClick={() => handleEdit(row)}
              >
                <MdEdit size={16} />
              </button>
            ) : null}
            {canDeleteRole ? (
              <button
                type="button"
                title="Delete"
                className="btn btn-sm p-1 lh-1 text-danger border-0 rounded-2"
                style={{ backgroundColor: 'var(--hc-status-danger-bg)' }}
                onClick={() => handleDeleteClick(row)}
              >
                <MdDelete size={16} />
              </button>
            ) : null}
          </div>
        ),
      });
    }

    return cols;
  }, [isSuperAdmin, handleView, handleEdit, handleDeleteClick, canViewRole, canEditRole, canDeleteRole]);

  return (
    <>
      <PageTitle title="Roles & Permissions - Aegis 360"/>
      <div className="d-flex flex-md-row flex-column align-items-start justify-content-between mb-4">
        <div>
          <h4 className="fw-bold mb-1 hc-text-primary fs-3">Roles &amp; Permissions</h4>
          <p className="text-secondary mb-0 small">
            Manage user roles and permission groups across the platform
          </p>
        </div>
        {canCreateRole ? (
          <button
            onClick={handleCreateRole}
            type="button"
            className="btn hc-bg-primary mt-md-0 mt-2 w-md-auto btn-sm text-white fw-semibold d-flex align-items-center justify-content-center gap-1 px-3 py-2 rounded-3 text-nowrap"
          >
            <MdAdd size={20} />
            <span>Create Role</span>
          </button>
        ) : null}
      </div>

      {loading ? (
        <div className="row g-3 mb-3">
          {[1, 2, 3].map((_, i) => (
            <div key={i} className="col-12 col-sm-6 col-lg-4">
              <div className="bg-white p-4 rounded-4 shadow">
                <div className="skeleton mb-2" style={{ height: 16, width: '60%' }} />
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 28, width: '40%' }} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="row g-3 mb-3">
          {stats.map((stat) => (
            <div key={stat.label} className="col-12 col-sm-6 col-lg-4">
              <div className="position-relative overflow-hidden bg-white p-4 rounded-4 shadow p-3 d-flex align-items-center gap-3 h-100 hc-list-kpi-card">
                <div
                  className={`position-absolute start-0 top-0 h-100 border-start border-5 ${stat.accentClass}`}
                />
                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-3"
                  style={{ width: 44, height: 44, backgroundColor: stat.iconBg }}
                >
                  {stat.icon}
                </div>
                <div className="d-flex flex-column fw-bold lh-sm">
                  <span className="text-dark" style={{ fontSize: '0.85rem' }}>
                    {stat.label}
                  </span>
                  <span className={`fw-bold fs-5 ${stat.valueClass}`}>
                    {stat.value}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-3 shadow-lg p-3 p-md-4">
        <div className="row g-2 mb-3">
          <div className="col-12 col-md-9">
            <div className="position-relative">
              <span
                className="position-absolute top-50 translate-middle-y d-inline-flex align-items-center justify-content-center text-secondary pe-none"
                style={{ left: '12px' }}
              >
                <MdSearch size={18} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm hc-input"
                placeholder="Search Role By Name"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                style={{ paddingLeft: '36px', paddingRight: '36px', height: '40px' }}
              />
              {search ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                  className="btn btn-link bg-danger p-0 position-absolute top-50 translate-middle-y text-light rounded-circle"
                  style={{ right: '8px', lineHeight: 0 }}
                >
                  <MdClose size={18} />
                </button>
              ) : null}
            </div>
          </div>

          <div className="col-12 col-md-3">
              <SearchableDropdown
                placeholder="Select status"
                options={STATUS_DROPDOWN_OPTIONS}
                value={status}
                onChange={setStatus}
                showSearch={true}
              />   

            {/* <FormControl fullWidth size="small">
              <InputLabel id="role-status-label">Status</InputLabel>
              <Select
                labelId="role-status-label"
                label="Status"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl> */}
          </div>
        </div>

        <div className="bg-white p-3 rounded shadow">
          {loading ? (
            <div>
              {[...Array(8)].map((_, i) => (
                <div key={i} className="d-flex gap-3 align-items-center mb-3">
                  <div className="skeleton position-relative overflow-hidden rounded-2 w-25" style={{ height: 16 }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '20%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '10%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '15%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '20%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '10%' }} />
                </div>
              ))}
            </div>
          ) : (
            <DataGrid
              columns={columns}
              rows={filteredRows}
              defaultRowsPerPage={10}
              stickyHeader
              getRowId={(row) => row.Id}
            />
          )}
        </div>
      </div>

      <DeleteModel
        isOpen={Boolean(deleteTarget)}
        title="Delete Role"
        entityLabel="role"
        itemName={deleteTarget?.Name || ''}
        description="You are about to remove this role from the platform. This action is permanent."
        confirmText="Delete Role"
        isLoading={deleteLoading}
        errorMessage={deleteError}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
}